import * as t from "io-ts"

import {
    BASIC_FORM,
    SINGLE_ARRAY_FORM,
    RECOGNISED_FORM,
} from "../../../../components/submitApplication/single/formGenerator/formClassKeys"
import {
    ATTACHMENT_INPUT_01_GENERAL,
    ATTACHMENT_INPUT_01_IMAGES,
    RECOGNISED_INPUT_ID_KW,
    SELECT_DEVICE_RECOGNISED_FORM_01,
    SELECT_EXISTING_DEVICE_RECOGNISED_FORM_01,
} from "../../../../components/submitApplication/single/recognisedForms/recongisedFormKeys"
import { NESTED_INPUT_FORM_DISCRIMINATED_UNION_01 } from "../../../../components/submitApplication/single/recognisedInputs/recognisedInputKeys"

// ************************************
// Section 1: Basic for spec generator
// ************************************

const createBasicFormSpec = <T extends t.Mixed>(
    properties: T
): t.IntersectionC<
    [
        t.TypeC<{
            title: t.StringC
            subtitle: t.StringC
            properties: T
            formClass: t.LiteralC<"BASIC FORM">
        }>,
        t.PartialC<{
            order: t.NumberC
        }>,
    ]
> =>
    t.intersection([
        t.type({
            title: t.string,
            subtitle: t.string,
            properties,
            formClass: t.literal(BASIC_FORM),
        }),
        t.partial({
            order: t.number,
        }),
    ])

// ************************************
// Section 2: TS-IO Models
// ************************************

const DisplayIf = t.union([
    t.null,
    t.strict({
        property: t.string,
        value: t.union([t.string, t.boolean]),
    }),
])

const Pattern = t.strict({
    value: t.string,
    message: t.string,
})

const DerivedValue = t.strict({
    source: t.string,
    sourceOutputMappings: t.record(t.string, t.string),
})

// Core input definition spec
const JsonSchemaExtra = t.strict({
    title: t.string,
    type: t.string,
    placeholderText: t.string,
    required: t.boolean,
    options: t.union([t.null, t.record(t.string, t.any)]),
    visible: t.boolean,
    default: t.string,
    displayIf: DisplayIf,
    pattern: t.union([t.null, Pattern]),
    tooltip: t.union([t.null, t.string]),
    fixedValue: t.union([t.null, t.string]),
    derivedValue: t.union([t.null, DerivedValue]),
    disableDuringAmendment: t.boolean,
})

// Input spec for a single input
export const BasicInputSpec = t.strict({
    title: t.string,
    json_schema_extra: JsonSchemaExtra,
})

// Basic form spec less the NestedDiscrimUnion01InputSpec union
const NestedDiscrimUnionBasicFormSpec = createBasicFormSpec(
    t.record(t.string, BasicInputSpec)
)

const NestedDiscrimUnion01InputSpec = t.intersection([
    BasicInputSpec,
    t.strict({
        oneOf: t.array(NestedDiscrimUnionBasicFormSpec),
        discriminator: t.strict({
            mapping: t.record(t.string, t.unknown),
            propertyName: t.string,
        }),
    }),
])

// Form properties are the specific fields required in the form
const FormProperties = t.record(
    t.string,
    t.union([NestedDiscrimUnion01InputSpec, BasicInputSpec]) // Mutating the order of this union will trigger bugs
)

// Specification for each form within the application
// Order is optional as it is only used in form specs and in some
// form specs Basic form spec is a nested child
export const BasicFormSpec = createBasicFormSpec(FormProperties)

export const SingleArrayFormSpec = t.strict({
    title: t.string,
    subtitle: t.string,
    recognisedInputId: t.string,
    formClass: t.literal(SINGLE_ARRAY_FORM),
    minItems: t.number,
    items: BasicFormSpec,
    order: t.number,
})

const SelectDeviceFormSpec = t.strict({
    title: t.string,
    subtitle: t.string,
    formClass: t.literal(RECOGNISED_FORM),
    recognisedInputId: t.string,
    minItems: t.number,
    order: t.number,
    items: t.strict({
        oneOf: t.array(BasicFormSpec),
        discriminator: t.strict({
            mapping: t.record(t.string, t.unknown),
            propertyName: t.string,
        }),
    }),
})

const AttachmentRecognisedFormSpec = t.strict({
    title: t.string,
    subtitle: t.string,
    formClass: t.literal(RECOGNISED_FORM),
    recognisedInputId: t.string,
    minItems: t.number,
    order: t.number,
    maxItems: t.union([t.number, t.undefined]),
    items: t.strict({
        properties: t.record(
            t.string,
            t.strict({
                title: t.string,
            })
        ),
    }),
})

// Root level properties for the application. This contains recognised
// properties which do not require a form to be generated. For these,
// the schema information is not required. Anything fields outside
// of these recognised properties will be generated as a form.
export const ApplicationProperties = t.record(
    t.string,
    t.union([
        BasicFormSpec,
        AttachmentRecognisedFormSpec,
        SingleArrayFormSpec,
        SelectDeviceFormSpec,
    ])
)

// Instructions for how to merge properties
const CombineProperties = t.strict({
    title: t.string,
    subtitle: t.string,
    combines: t.array(t.string),
    order: t.number,
})

export const FixedInputs = t.record(t.string, t.unknown)
export type IFixedInputs = t.TypeOf<typeof FixedInputs>

// Overall spec for the application
export const ApplicationSpec = t.strict({
    title: t.string,
    properties: ApplicationProperties,
    fixedInputs: FixedInputs,
    combineProperties: t.record(t.string, CombineProperties),
})

// ************************************
// Section 3: Type conversions
// ************************************

// Convert ts-io models to interfaces
export type IDisplayIf = t.TypeOf<typeof DisplayIf>
export type IPattern = t.TypeOf<typeof Pattern>
export type IJsonSchemaExtra = t.TypeOf<typeof JsonSchemaExtra>
export type IBasicInputSpec = t.TypeOf<typeof BasicInputSpec>
export type INestedDiscimUnion01InputSpec = t.TypeOf<
    typeof NestedDiscrimUnion01InputSpec
>
export type IBasicFormSpec = t.TypeOf<typeof BasicFormSpec>
export type ICombineProperties = t.TypeOf<typeof CombineProperties>
export type IAttachmentRecognisedFormSpec = t.TypeOf<
    typeof AttachmentRecognisedFormSpec
>
export type IFormProperties = t.TypeOf<typeof FormProperties>
export type IApplicationProperties = t.TypeOf<typeof ApplicationProperties>
export type IApplicationSpec = t.TypeOf<typeof ApplicationSpec>
export type ISingleArrayFormSpec = t.TypeOf<typeof SingleArrayFormSpec>
export type ISelectDeviceFormSpec = t.TypeOf<typeof SelectDeviceFormSpec>
export type IPossibleApplicationForms =
    | IFormProperties
    | IAttachmentRecognisedFormSpec
    | ISingleArrayFormSpec
    | ISelectDeviceFormSpec
export type IPossibleRecognisedFormSpecs =
    | IAttachmentRecognisedFormSpec
    | ISelectDeviceFormSpec

type IPossibleInputSpecs = IBasicInputSpec | INestedDiscimUnion01InputSpec

// Mapper return type
export interface IApplicationStepIndexMap {
    [key: number]: string
}
export interface IApplicationForms {
    [key: string]: IPossibleApplicationForms
}
export interface IFormTitlesIndexMap {
    [key: string]: { title: string; subtitle: string }
}

export interface IGetSchemaMapper {
    rawApplicationSpec: IApplicationSpec
    applicationStepIndexMap: IApplicationStepIndexMap
    applicationForms: IApplicationForms
    formTitlesIndexMap: IFormTitlesIndexMap
    fixedInputs: IFixedInputs
    jurisdiction?: string // Make jurisdiction optional for backward compatibility
}

export interface IAttachment {
    fileUrl: string
}

type FormSpecs =
    | IBasicFormSpec
    | IAttachmentRecognisedFormSpec
    | ISingleArrayFormSpec
    | ISelectDeviceFormSpec

// ************************************
// Section 4: Type guards for differentiating form classes
// ************************************

export const isBasicFormClass = (obj: FormSpecs): obj is IBasicFormSpec =>
    obj.formClass === BASIC_FORM

export const isRecognisedFormClass = (
    obj: FormSpecs
): obj is IPossibleRecognisedFormSpecs => obj.formClass === RECOGNISED_FORM

export const isSingleArrayFormClass = (
    obj: FormSpecs
): obj is ISingleArrayFormSpec => obj.formClass === SINGLE_ARRAY_FORM

// ************************************
// Section 5: Type guards for differentiating form types
// ************************************

export const isRecognisedForm = (
    obj: IPossibleApplicationForms
): obj is IPossibleRecognisedFormSpecs => obj?.formClass === RECOGNISED_FORM

export const isSingleArrayForm = (
    obj: IPossibleApplicationForms
): obj is ISingleArrayFormSpec => obj?.formClass === SINGLE_ARRAY_FORM

// ************************************
// Section 6: Type guards for differentiating recognised form types
// ************************************

export const isSelectDeviceRecognisedForm = (
    obj: IPossibleRecognisedFormSpecs
): obj is ISelectDeviceFormSpec =>
    obj[RECOGNISED_INPUT_ID_KW] === SELECT_DEVICE_RECOGNISED_FORM_01 ||
    obj[RECOGNISED_INPUT_ID_KW] === SELECT_EXISTING_DEVICE_RECOGNISED_FORM_01

export const isAttachmentRecognisedForm = (
    obj: IPossibleRecognisedFormSpecs
): obj is IAttachmentRecognisedFormSpec =>
    obj[RECOGNISED_INPUT_ID_KW] === ATTACHMENT_INPUT_01_GENERAL ||
    obj[RECOGNISED_INPUT_ID_KW] === ATTACHMENT_INPUT_01_IMAGES

export const isInputSpecNestedDiscrimUnion01 = (
    inputSpec: IPossibleInputSpecs
): inputSpec is INestedDiscimUnion01InputSpec =>
    inputSpec.json_schema_extra.type ===
    NESTED_INPUT_FORM_DISCRIMINATED_UNION_01

// ************************************
// Section 7: Other type guards
// ************************************

export const isPatternDefined = (
    obj: IJsonSchemaExtra
): obj is IJsonSchemaExtra & { pattern: IPattern } => obj.pattern !== null
